/* ================================================================
   LIVEFAST GALLERY — FEAT-008 "Gallery Mode", first build (cp34)
   Node-graph exploration of the video archive. Reads
   window.LF_CONFIG.videos (same assets/videos.json used by
   /pages/dvd and the homepage) via window.LF_VIDEOS_READY — no
   separate content file, no duplicate fetch.

   TWO RENDER PATHS, decided once per load/resize:
     - renderCanvas()  desktop / hover-capable: absolutely-positioned
       nodes scattered inside a horizontally-scrolling canvas, with
       thin SVG lines threading each content "section" (recent /
       archive / unreleased) into its own loose cluster-path.
     - renderMobile()  touch / narrow viewport: plain CSS grid, same
       nodes, same click target, no scroll-hijack, no SVG lines.
   The split is decided by isMobileLayout() — width <= 768 OR no
   real hover capability — never by two independently-drifting CSS
   breakpoints, so there's exactly one source of truth for which
   layout a given visit gets.

   INTENTIONAL RANDOMNESS: node positions are re-rolled (Math.random,
   no fixed seed) on every fresh render — this is not a bug. Per the
   build doc §18.3 (FEAT-003, "Transmissions"): "everything about the
   site should feel random and different every visit." Gallery Mode
   is the first feature to actually act on that philosophy rather
   than just referencing it. A resize re-render also re-rolls
   positions — acceptable and mentioned in the checkpoint doc as a
   known, deliberate side effect, not chased as a bug.

   FAILURE SAFETY: every entry point wrapped in safeRun (same pattern
   as theme.js / lf-magazine.js) so a failure here can never take
   down the rest of the page. Missing/empty videos.json shows the
   honest #lf-gallery-empty state instead of a broken canvas.
================================================================ */
(function () {
  'use strict';

  function safeRun(label, fn) {
    try { fn(); } catch (e) { console.warn('[LF:gallery:' + label + ']', e); }
  }

  document.addEventListener('DOMContentLoaded', function () {
    safeRun('init', function () {
      var root = document.getElementById('lf-gallery-root');
      if (!root) return; // gallery_enabled was off, or wrong template — nothing to do

      if (window.__lfGalleryBound) return;
      window.__lfGalleryBound = true;

      var scrollWrap  = document.getElementById('lf-gallery-scroll');
      var canvas      = document.getElementById('lf-gallery-canvas');
      var linesSvg    = document.getElementById('lf-gallery-lines');
      var nodesLayer  = document.getElementById('lf-gallery-nodes');
      var emptyState  = document.getElementById('lf-gallery-empty');
      var mobileList  = document.getElementById('lf-gallery-mobile');
      var modal       = document.getElementById('lf-gallery-modal');

      if (!scrollWrap || !canvas || !mobileList || !modal) return;

      var currentItems = [];
      var lastFocused = null;
      var SVG_NS = 'http://www.w3.org/2000/svg';

      /* ── layout mode decision — single source of truth ── */
      function isMobileLayout() {
        var noHover = window.matchMedia && !window.matchMedia('(hover: hover)').matches;
        return window.innerWidth <= 768 || noHover;
      }

      /* ── escape text defensively (same helper as lf-magazine.js) ── */
      function escapeHTML(str) {
        var div = document.createElement('div');
        div.textContent = String(str == null ? '' : str);
        return div.innerHTML;
      }

      /* ────────────────────────────────────────────────────────────
         MODAL — one shared instance, populated per item on open.
         Handles all three content shapes in videos.json:
           - item.id       → real YouTube video, link out (no embed —
                              keeps this file lightweight, matches the
                              "visuals-only, not a video-embed page"
                              framing; the DVD page already owns the
                              full inline-player experience)
           - item.igUrl     → Instagram post, link out
           - item.videoUrl  → real CDN mp4 (the unreleased/EXCLUSIVE
                              website-only footage) → plays inline,
                              since this IS the room for that footage
         ──────────────────────────────────────────────────────────── */
      function openModal(item) {
        var mediaEl = modal.querySelector('.lf-gallery-modal-media');
        var tagEl   = modal.querySelector('.lf-gallery-modal-tag');
        var titleEl = modal.querySelector('.lf-gallery-modal-title');
        var linkEl  = modal.querySelector('.lf-gallery-modal-link');
        if (!mediaEl || !tagEl || !titleEl || !linkEl) return;

        mediaEl.innerHTML = '';
        tagEl.textContent = item.type || '';
        titleEl.textContent = item.title + (item.year ? ' — ' + item.year : '');

        if (item.videoUrl) {
          var vid = document.createElement('video');
          vid.src = item.videoUrl;
          vid.controls = true;
          vid.muted = true;
          vid.setAttribute('playsinline', '');
          if (item.thumbnail) {
            vid.setAttribute('poster', item.thumbnail);
          } else if (window.LFJ_THUMB_FALLBACK) {
            /* CP70.11 — was `item.thumbnail || ''` (a blank poster);
               autoplay below covers most of the gap already, but this
               avoids a flash of nothing while it starts. */
            window.LFJ_THUMB_FALLBACK.resolve(item, function (url) { vid.setAttribute('poster', url); });
          }
          mediaEl.appendChild(vid);
          safeRun('modal autoplay', function () { vid.play().catch(function () {}); });
          linkEl.hidden = true;
        } else {
          var img = document.createElement('img');
          img.alt = '';
          if (item.thumbnail) {
            img.src = item.thumbnail;
          } else if (window.LFJ_THUMB_FALLBACK) {
            /* CP70.11 — was `item.thumbnail || ''`, a guaranteed broken
               <img src=""> for any YouTube/Instagram entry with no
               thumbnail set. */
            window.LFJ_THUMB_FALLBACK.resolve(item, function (url) { img.src = url; });
          }
          mediaEl.appendChild(img);
          if (item.id) {
            linkEl.href = 'https://www.youtube.com/watch?v=' + item.id;
            linkEl.textContent = 'Watch on YouTube ↗';
            linkEl.hidden = false;
          } else if (item.igUrl) {
            linkEl.href = item.igUrl;
            linkEl.textContent = 'View on Instagram ↗';
            linkEl.hidden = false;
          } else {
            linkEl.hidden = true;
          }
        }

        lastFocused = document.activeElement;
        modal.classList.add('is-open');
        modal.setAttribute('aria-hidden', 'false');
        document.body.style.overflow = 'hidden';
        var closeBtn = modal.querySelector('.lf-gallery-modal-close');
        if (closeBtn) closeBtn.focus();
      }

      function closeModal() {
        modal.classList.remove('is-open');
        modal.setAttribute('aria-hidden', 'true');
        document.body.style.overflow = '';
        var vid = modal.querySelector('video');
        if (vid) { safeRun('modal pause', function () { vid.pause(); }); }
        if (lastFocused && typeof lastFocused.focus === 'function') lastFocused.focus();
      }

      modal.querySelectorAll('[data-gallery-close]').forEach(function (el) {
        el.addEventListener('click', closeModal);
      });
      document.addEventListener('keydown', function (e) {
        if (e.key === 'Escape' && modal.classList.contains('is-open')) closeModal();
      });

      /* ── shared node factory — identical button for both layouts ── */
      function makeNodeButton(item, index) {
        var btn = document.createElement('button');
        btn.type = 'button';
        btn.className = 'lf-gallery-node' + (item.type === 'EXCLUSIVE' ? ' lf-gallery-node--exclusive' : '');
        btn.setAttribute('data-index', index);
        btn.setAttribute('aria-label', (item.title || 'Untitled') + (item.year ? ' — ' + item.year : ''));

        var thumb = document.createElement('span');
        thumb.className = 'lf-gallery-node-thumb';
        if (item.thumbnail) {
          thumb.style.backgroundImage = 'url(' + item.thumbnail + ')';
        } else if (window.LFJ_THUMB_FALLBACK) {
          /* CP70.11 — this used to just do nothing for a thumbnail-less
             entry, leaving a blank node in the canvas/grid. This is the
             actual click target across both layouts (renderCanvas +
             renderMobile both call this), so it's the highest-value fix
             in this file. */
          window.LFJ_THUMB_FALLBACK.resolve(item, function (url) { thumb.style.backgroundImage = 'url(' + url + ')'; });
        }
        btn.appendChild(thumb);

        var label = document.createElement('span');
        label.className = 'lf-gallery-node-label';
        label.textContent = item.title || '';
        btn.appendChild(label);

        return btn;
      }

      function bindNodeClicks(container, items) {
        container.addEventListener('click', function (e) {
          var btn = e.target.closest ? e.target.closest('.lf-gallery-node') : null;
          if (!btn) return;
          var idx = parseInt(btn.getAttribute('data-index'), 10);
          var item = items[idx];
          if (item) openModal(item);
        });
      }

      /* ────────────────────────────────────────────────────────────
         DESKTOP — node-graph canvas
         ──────────────────────────────────────────────────────────── */
      function layoutPositions(items, width, height) {
        var bandCenter = { recent: 0.24, archive: 0.52, unreleased: 0.8 };
        var positions = [];
        items.forEach(function (item, i) {
          var band = bandCenter.hasOwnProperty(item.section) ? bandCenter[item.section] : 0.5;
          var baseX = 90 + (i * (width - 180) / Math.max(items.length - 1, 1));
          var jitterX = (Math.random() - 0.5) * 60;
          var jitterY = (Math.random() - 0.5) * height * 0.3;
          var x = Math.min(Math.max(baseX + jitterX, 60), width - 60);
          var y = Math.min(Math.max(band * height + jitterY, 60), height - 60);
          positions.push({ x: x, y: y });
        });
        return positions;
      }

      function drawLines(items, positions) {
        while (linesSvg.firstChild) linesSvg.removeChild(linesSvg.firstChild);
        var bySection = {};
        items.forEach(function (item, i) {
          var key = item.section || 'other';
          (bySection[key] = bySection[key] || []).push(i);
        });
        Object.keys(bySection).forEach(function (key) {
          var idxs = bySection[key];
          for (var k = 0; k < idxs.length - 1; k++) {
            var a = positions[idxs[k]];
            var b = positions[idxs[k + 1]];
            var line = document.createElementNS(SVG_NS, 'line');
            line.setAttribute('x1', a.x); line.setAttribute('y1', a.y);
            line.setAttribute('x2', b.x); line.setAttribute('y2', b.y);
            line.setAttribute('class', 'lf-gallery-line lf-gallery-line--' + key);
            linesSvg.appendChild(line);
          }
        });
      }

      function renderCanvas(items) {
        mobileList.hidden = true;
        scrollWrap.hidden = false;

        var height = scrollWrap.clientHeight || 420;
        var width = Math.max(Math.round(window.innerWidth * 1.6), items.length * 190);

        canvas.style.width = width + 'px';
        canvas.style.height = height + 'px';
        linesSvg.setAttribute('width', width);
        linesSvg.setAttribute('height', height);
        linesSvg.setAttribute('viewBox', '0 0 ' + width + ' ' + height);

        var positions = layoutPositions(items, width, height);
        drawLines(items, positions);

        while (nodesLayer.firstChild) nodesLayer.removeChild(nodesLayer.firstChild);
        items.forEach(function (item, i) {
          var btn = makeNodeButton(item, i);
          btn.style.left = positions[i].x + 'px';
          btn.style.top = positions[i].y + 'px';
          nodesLayer.appendChild(btn);
        });

        bindNodeClicks(nodesLayer, items);

        /* wheel → horizontal scroll convenience (only when the
           gesture reads as primarily vertical, so trackpad users
           already scrolling horizontally are left alone) */
        scrollWrap.addEventListener('wheel', function (e) {
          if (Math.abs(e.deltaY) > Math.abs(e.deltaX)) {
            e.preventDefault();
            scrollWrap.scrollLeft += e.deltaY;
          }
        }, { passive: false });

        /* keyboard panning when the scroll region itself has focus */
        scrollWrap.addEventListener('keydown', function (e) {
          if (e.key === 'ArrowRight') { scrollWrap.scrollLeft += 140; }
          else if (e.key === 'ArrowLeft') { scrollWrap.scrollLeft -= 140; }
        });
      }

      /* ────────────────────────────────────────────────────────────
         MOBILE / NO-HOVER — plain grid fallback
         ──────────────────────────────────────────────────────────── */
      function renderMobile(items) {
        scrollWrap.hidden = true;
        mobileList.hidden = false;
        while (mobileList.firstChild) mobileList.removeChild(mobileList.firstChild);
        items.forEach(function (item, i) {
          var btn = makeNodeButton(item, i);
          mobileList.appendChild(btn);
        });
        bindNodeClicks(mobileList, items);
      }

      /* ────────────────────────────────────────────────────────────
         ENTRY POINT
         ──────────────────────────────────────────────────────────── */
      function ready(videos) {
        /* CP70.11 — was `v && v.thumbnail && v.title`, silently dropping
           any video with no thumbnail from this whole feature (not just
           rendering it blank — it never even reached renderCanvas/
           renderMobile). The thumbnail is now generated by
           LFJ_THUMB_FALLBACK inside makeNodeButton()/openModal() above,
           so it's no longer a precondition for showing up here at all. */
        var items = (videos || []).filter(function (v) { return v && v.title; });
        currentItems = items;

        if (!items.length) {
          if (emptyState) emptyState.hidden = false;
          scrollWrap.hidden = true;
          mobileList.hidden = true;
          return;
        }
        if (emptyState) emptyState.hidden = true;

        if (isMobileLayout()) {
          renderMobile(items);
        } else {
          renderCanvas(items);
        }
      }

      var resizeTimer;
      window.addEventListener('resize', function () {
        clearTimeout(resizeTimer);
        resizeTimer = setTimeout(function () {
          if (currentItems.length) ready(currentItems);
        }, 250);
      });

      if (window.LF_VIDEOS_READY && typeof window.LF_VIDEOS_READY.then === 'function') {
        window.LF_VIDEOS_READY
          .then(function () { ready(window.LF_CONFIG && window.LF_CONFIG.videos); })
          .catch(function () { ready([]); });
      } else {
        ready((window.LF_CONFIG && window.LF_CONFIG.videos) || []);
      }
    });
  });
})();
